import { ISessionStore } from 'library/ts/utilities/interfaces/ISessionStore';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';

export default class SessionStore implements ISessionStore {

	private static _instance: ISessionStore = new SessionStore();

	#available: boolean;

	#logger: ILogger;

	constructor() {
		if ( SessionStore._instance ) {
			throw new Error( this.name + ' is a Singleton and can only be accessed through ' + this.name + '.getInstance()' );
		}
		SessionStore._instance = this;

		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', this.name + ':' ] } );

		// check if sessionStorage is available
		const test: string = 'test' + new Date().getTime();

		try {
			sessionStorage.setItem( test, test );
			sessionStorage.removeItem( test );
			this.#available = true;
		} catch ( error ) {
			this.#available = false;
		}
	}

	public static getInstance(): ISessionStore {
		return SessionStore._instance;
	}

	get name(): string {
		return 'SessionStore';
	}

	/**
	 * @description Returns session storage availability flag.
	 * @return {boolean}
	 */
	get available(): boolean {
		return this.#available;
	}

	/**
	 * @description Sets session storage for a key
	 * @param {string} key
	 * @param value
	 */
	set = ( key: string, value: any ): void => {
		try {
			sessionStorage.setItem( key, JSON.stringify( value ) );
		} catch ( error ) {
			this.#logger.warn( 'set:', value, 'ERROR:', error );
		}
	};

	/**
	 * @description Gets session storage value for a key
	 * @param {string} key
	 * @return {any | undefined}
	 */
	get = ( key: string ): any | undefined => {
		let value;

		try {
			value = sessionStorage?.getItem( key );
		} catch ( error ) {
			this.#logger.warn( `get: ${key}: ${value}`, 'ERROR:', error );
		}

		if ( typeof value !== 'string' ) {
			return undefined;
		}

		try {
			return JSON.parse( value );
		} catch ( error ) {
			return value || undefined;
		}
	};

	/**
	 * @description Removes session storage item by key
	 * @param {string} key
	 */
	remove = ( key: string ): void => {
		try {
			sessionStorage.removeItem( key );
		} catch ( error ) {
			this.#logger.warn( 'remove:', key, 'ERROR:', error );
		}
	};

	/**
	 * @description Clears session storage
	 */
	clear = (): void => {
		try {
			sessionStorage.clear();
		} catch ( error ) {
			this.#logger.warn( 'clear:', 'ERROR:', error );
		}
	};

}